import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useBillsList, useBillsSummary, useBillFilters } from '../hooks';
import { useBillColumns, BillsDataTable, BillDialog, BillsSummary } from '../components';
import type { Bill } from '../types';

export function BillsPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { filters, updateFilter, updateFilters, resetFilters, setPage, setLimit } = useBillFilters();

  const { data, isLoading } = useBillsList(filters);
  // Billed / paid totals for the WHOLE filtered set (not the visible page) —
  // same filters, paging stripped inside the hook. Only meaningful once a
  // contract is picked (a contract fixes the currency), so the request waits
  // for one instead of fetching a mixed-currency total nobody can read.
  const hasContract = filters.contractId != null;
  const { data: summary, isLoading: isSummaryLoading } = useBillsSummary(
    filters,
    hasContract
  );
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Bill | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (bill: Bill) => {
    setEditing(bill);
    setDialogOpen(true);
  };

  const handleSort = (field: string) => {
    if (filters.orderBy !== field) {
      updateFilters({ orderBy: field, order: 'asc' });
    } else {
      if (filters.order === 'asc') {
        updateFilters({ order: 'desc' });
      } else if (filters.order === 'desc') {
        updateFilters({ orderBy: undefined, order: undefined });
      } else {
        updateFilters({ order: 'asc' });
      }
    }
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);

  const columns = useBillColumns({
    orderBy: filters.orderBy ?? '',
    order: filters.order ?? '',
    onSort: handleSort,
    filters,
    onEdit: openEdit,
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('bills')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('hourly-package', 'get') && (
            <Link to="/app/hourly-packages">
              <Button variant="outline">{t('hourlyPackages')}</Button>
            </Link>
          )}
          {hasAccess('success-package', 'get') && (
            <Link to="/app/success-packages">
              <Button variant="outline">{t('successPackages')}</Button>
            </Link>
          )}
          {hasAccess('bill', 'create') && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {t('billForm.create')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <BillsSummary
          buckets={summary?.buckets}
          hasContract={hasContract}
          isLoading={isSummaryLoading}
        />

        <BillsDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          isLoading={isLoading}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>

      <BillDialog open={dialogOpen} onOpenChange={setDialogOpen} bill={editing} />
    </div>
  );
}
